import i18n from './i18n';
import { getGtinDescription } from './gtin-metadata';
import { getMinAndMaxQuantity } from '@/contexts/item-context-utils';
import { getOrderItemPricesFieldsToUse } from './money';
import { getPackDisplayString } from './item';
import { getParamsFromNameValue } from './salt-item';
import {
  GTINMetadata,
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import {
  NewOrder_OrderFieldsFragment,
  OrderSummary_ItemListFieldsFragment,
  OrderSummary_ValidateOrderMutation,
} from '@/types';
import { PackUnit } from '@/enums';
import {
  QuantityMapType,
  VariantPackDetailMap,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { useAvailabilityStatusEnabled } from '@/hooks/use-add-to-order-disabled';
import { useCallback } from 'react';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

/**
 * Merges notes from an updated order (e.g. from updateCart) into validateOrder
 * state so the order summary list updates without refetching.
 */
export function mergeOrderNotesIntoValidateOrderData(
  prev: OrderSummary_ValidateOrderMutation | undefined,
  updatedOrder: NewOrder_OrderFieldsFragment,
): OrderSummary_ValidateOrderMutation | undefined {
  if (!prev?.validateOrder?.order?.orders_items) {
    return prev;
  }
  const notesByVariantPack = new Map(
    updatedOrder.orders_items.map((oi) => [
      oi.variants_pack.uuid,
      oi.notes ?? null,
    ]),
  );
  const mergedItems = prev.validateOrder.order.orders_items.map((oi) => {
    const vpUuid = oi.variants_pack?.uuid;
    if (!vpUuid) {
      return oi;
    }
    const updatedNotes = notesByVariantPack.get(vpUuid);
    if (updatedNotes === undefined) {
      return oi;
    }
    if ((oi.notes ?? null) === updatedNotes) {
      return oi;
    }
    return { ...oi, notes: updatedNotes };
  });
  return {
    ...prev,
    validateOrder: {
      ...prev.validateOrder,
      order: {
        ...prev.validateOrder.order,
        orders_items: mergedItems,
      },
    },
  };
}

export const useAdaptOrderItemToItemListOption = () => {
  const { displayItemCode, displayItemPrices, gtinSettings } =
    useItemDisplayOptions();
  const { currencyCode, getVariantPackInfo } = useVariantPackInfoContext();

  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();

  return useCallback(
    (orderItem: OrderSummary_ItemListFieldsFragment): ItemListOption => {
      const itemPriceFields = getOrderItemPricesFieldsToUse(orderItem);

      let description = orderItem.variants_pack?.item.description;
      let displayName = orderItem.restaurant_display_name;
      let photoURLs = orderItem.variants_pack!.item.photo_url_list ?? [];

      // Use SALT enabled if available, otherwise use GTIN metadata if available
      if (saltApiIntegrationEnabled) {
        if (orderItem.variants_pack.salt) {
          description =
            getParamsFromNameValue(
              orderItem.variants_pack.salt.basicInformation.descriptions,
            ).Description ?? description;
          photoURLs = [
            ...new Set(
              orderItem.variants_pack.salt.images.map((image) => image.uri),
            ),
          ];
        }
      } else if (gtinSettings) {
        const gtinMetadata = (orderItem.variants_pack.gtin_metadata?.[0]
          ?.metadata ?? null) as GTINMetadata | null;
        if (gtinMetadata) {
          description = getGtinDescription(
            gtinSettings,
            gtinMetadata,
            description,
          );
        }
      }

      const pack = orderItem.pricing_pack || orderItem.pack;
      const variantPackInfo = getVariantPackInfo(orderItem.variants_pack.uuid);
      // always use the base unit_price_at_order_in_micros here to show difference between
      // promo price and unit price.
      // When the unit_price_at_order_micros doesn't exist, it's probably an offline order
      // and the price is retrieved from the variant pack info.
      const baseUnitPriceInMicros = displayItemPrices
        ? orderItem.order_item_prices?.unit_price_at_order_micros
          ? orderItem.order_item_prices?.unit_price_at_order_micros
          : variantPackInfo?.priceInMicros
        : null;

      const { maximumQuantity, minimumQuantity } =
        getMinAndMaxQuantity(variantPackInfo);

      const metadata: ItemListOptionMetadata = {
        availabilityStatus:
          availabilityStatusEnabled && variantPackInfo?.availabilityStatus
            ? variantPackInfo.availabilityStatus
            : null,
        availableQuantity: null,
        baseUnit: (pack.base_unit as PackUnit) || null,
        category: null,
        currencyCode: itemPriceFields?.currencyCode || currencyCode,
        description,
        displayName,
        earliestFulfillmentAt: orderItem.earliest_fulfillment_at,
        externalID: displayItemCode
          ? orderItem.variants_pack.external_item_id || null
          : null,
        itemUUID: orderItem.variants_pack!.item.uuid,
        maximumQuantity,
        minimumQuantity,
        modifierString: (orderItem.orders_items_modifier_options || [])
          .map((opt) => opt.name)
          .join(', '),
        notes: orderItem.notes,
        outOfStockUUIDs: new Set(),
        packDisplayString: getPackDisplayString({
          maxUnitSize: pack.max_unit_size,
          minUnitSize: pack.min_unit_size,
          unit: pack.unit as PackUnit,
        }),
        packUnit: pack.unit as PackUnit,
        photoURLs,
        prebookConfig: orderItem.variants_pack.prebook_config,
        promotion: null,
        promotionPricePerUnitInMicros: displayItemPrices
          ? (orderItem.order_item_prices?.unit_discount_price_at_order_micros ??
            null)
          : null,
        skus: [],
        unitCount: pack.unit_count,
        unitPriceInMicros: baseUnitPriceInMicros,
        upcList: null,
        variantPackMetadata: orderItem.variants_pack.metadata,
        variantPackUUID: orderItem.variants_pack!.uuid,
      };

      return {
        metadata: {
          ...metadata,
          skus: [metadata],
        },
        title: displayName,
        value: orderItem.uuid,
      };
    },
    [
      availabilityStatusEnabled,
      currencyCode,
      displayItemCode,
      displayItemPrices,
      getVariantPackInfo,
      gtinSettings,
      saltApiIntegrationEnabled,
    ],
  );
};

export const getItemWeightText = (
  quantityMap: QuantityMapType,
  variantPackDetailMap: VariantPackDetailMap,
  variantPackUUIDs?: string[],
) => {
  const variantPackUUIDsToUse = variantPackUUIDs || Object.keys(quantityMap);

  const totalWeight = variantPackUUIDsToUse.reduce((acc, variantPackUUID) => {
    const detail = variantPackDetailMap[variantPackUUID];
    const itemWeight = detail?.metadata?.additional_info?.case_weight;
    if (typeof itemWeight === 'number') {
      const quantity = quantityMap[variantPackUUID] || 0;
      const totalWeightOfItem = quantity * itemWeight;
      return acc + totalWeightOfItem;
    }
    return acc;
  }, 0);

  if (totalWeight === 0) {
    return null;
  }
  return i18n.t(totalWeight === 1 ? 'lb' : 'lbs', {
    weight: parseFloat(totalWeight.toFixed(2)),
  });
};

export const getPalletCountText = (
  quantityMap: QuantityMapType,
  variantPackDetailMap: VariantPackDetailMap,
  variantPackUUIDs?: string[],
) => {
  const variantPackUUIDsToUse = variantPackUUIDs || Object.keys(quantityMap);
  const totalPalletCount = variantPackUUIDsToUse.reduce(
    (acc, variantPackUUID) => {
      const detail = variantPackDetailMap[variantPackUUID];
      const palletSize = detail?.metadata?.additional_info?.palletsize;
      // check for presence of pallesize and prevent division by zero
      if (palletSize) {
        const quantity = quantityMap[variantPackUUID] || 0;
        const amountOfPalletTakenUp = quantity / palletSize;
        return acc + amountOfPalletTakenUp;
      }
      return acc;
    },
    0,
  );

  if (totalPalletCount === 0) {
    return null;
  }

  return i18n.t(totalPalletCount === 1 ? 'pallet' : 'pallets', {
    count: parseFloat(totalPalletCount.toFixed(2)),
  });
};
